import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Empty, Progress, Segmented, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import type { DayResult, Job, JobFlight } from '../../../src/types.ts';
import { fmtDuration } from '../lib/format.ts';
import { exportCsv } from '../lib/export.ts';
import { cheapestPerAirline, dayCheapest, type MaxStops } from '../lib/flights.ts';
import { AirlineStrip } from './AirlineStrip.tsx';
import { CalendarGrid } from './CalendarGrid.tsx';
import { CheapestCard } from './CheapestCard.tsx';

export interface SearchMeta {
  title: string;
  pax: string;
  cabin: string;
  startedAt: string;
}

interface Props {
  job: Job | null;
  meta: SearchMeta | null;
  maxStops: MaxStops;
  onMaxStops: (v: MaxStops) => void;
  onOpenDay: (day: DayResult) => void;
  onBook: (flight: JobFlight, day: DayResult) => void;
}

const STOP_OPTIONS = [
  { label: 'Any stops', value: 'any' },
  { label: 'Direct', value: '0' },
  { label: '1 stop', value: '1' },
  { label: '2 stops', value: '2' },
];

export function ResultsPanel({ job, meta, maxStops, onMaxStops, onOpenDay, onBook }: Props) {
  // Tick every second while a search is running so the ETA counts down smoothly
  // between the (slower) job polls.
  const [now, setNow] = useState(() => Date.now());
  const isRunning = job?.status === 'running';
  useEffect(() => {
    if (!isRunning) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isRunning]);

  const best = useMemo(() => {
    if (!job) return null;
    let pick: { day: DayResult; flight: NonNullable<ReturnType<typeof dayCheapest>> } | null = null;
    for (const day of job.days) {
      const flight = dayCheapest(day, maxStops);
      if (flight && (!pick || flight.price < pick.flight.price)) pick = { day, flight };
    }
    return pick;
  }, [job, maxStops]);

  const airlines = useMemo(() => (job ? cheapestPerAirline(job.days, maxStops) : []), [job, maxStops]);

  if (!job || !meta) {
    return (
      <Card>
        <Empty
          description={
            <Space orientation="vertical" size={4}>
              <Typography.Text strong>Pick a route and a month, then hit Search.</Typography.Text>
              <Typography.Text type="secondary">
                Every etravel provider is asked for every day of the month. Click a day to see all fares, and book straight from there
                with your wallet.
              </Typography.Text>
            </Space>
          }
        />
      </Card>
    );
  }

  const { progress } = job;
  const pct = progress.daysTotal ? Math.round((progress.daysDone / progress.daysTotal) * 100) : 0;
  const running = job.status === 'running';

  // ETA from the average time-per-day since the job started. We wait for the
  // first day to finish before estimating, otherwise the rate is meaningless.
  const elapsedMs = now - Date.parse(job.createdAt);
  const remainingDays = Math.max(0, progress.daysTotal - progress.daysDone);
  const eta =
    !running || remainingDays === 0
      ? null
      : progress.daysDone > 0 && elapsedMs > 0
        ? fmtDuration((elapsedMs / progress.daysDone) * remainingDays)
        : 'estimating…';

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {meta.title}
          </Typography.Title>
          <Space size={6} style={{ marginTop: 8 }} wrap>
            <Tag>{meta.pax}</Tag>
            <Tag>{meta.cabin}</Tag>
            <Tag color="default">{running ? `Updated ${dayjs(job.updatedAt).format('HH:mm:ss')}` : `Finished ${dayjs(job.updatedAt).format('HH:mm:ss')}`}</Tag>
            {running && eta && <Tag color="processing">{eta === 'estimating…' ? 'ETA estimating…' : `ETA ~${eta}`}</Tag>}
          </Space>
        </div>
        <Space wrap>
          <Segmented
            options={STOP_OPTIONS}
            value={maxStops == null ? 'any' : String(maxStops)}
            onChange={(v) => onMaxStops(v === 'any' ? null : Number(v))}
          />
          <Button icon={<DownloadOutlined />} disabled={!job.days.some((d) => d.flights.length)} onClick={() => exportCsv(job, maxStops)}>
            CSV
          </Button>
        </Space>
      </div>

      <Progress
        percent={pct}
        status={job.status === 'error' ? 'exception' : job.status === 'cancelled' ? 'normal' : running ? 'active' : 'success'}
        format={() =>
          running
            ? `${progress.daysDone} / ${progress.daysTotal} days · ${progress.providersTotal} providers${eta ? (eta === 'estimating…' ? ' · ETA estimating…' : ` · ~${eta} left`) : ''}`
            : job.status === 'cancelled'
              ? `stopped after ${progress.daysDone} / ${progress.daysTotal} days`
              : `${progress.daysTotal} / ${progress.daysTotal} days`
        }
        percentPosition={{ align: 'end', type: 'outer' }}
      />

      {job.status === 'error' && job.error && <Alert type="error" showIcon message="Search failed" description={job.error} />}

      {job.status === 'cancelled' && (
        <Alert
          type="info"
          showIcon
          message="Search cancelled"
          description={`Showing the ${progress.daysDone} day${progress.daysDone === 1 ? '' : 's'} that finished before you stopped it.`}
        />
      )}

      {best && <CheapestCard day={best.day} flight={best.flight} onOpen={() => onOpenDay(best.day)} onBook={() => onBook(best.flight, best.day)} />}

      <AirlineStrip airlines={airlines} />

      <Card size="small">
        <CalendarGrid job={job} maxStops={maxStops} onOpenDay={onOpenDay} />
      </Card>
    </Space>
  );
}
